/**
 * The donor's currency selector, beside the amount (CON-314, SPEC 3.C2).
 * P3 UI-1: the forms with a custom-amount field (StandardForm, CrowdfundingForm)
 * use `CurrencyAffixPicker` below, inside that field, instead; the forms without
 * one (ProductForm, TicketSelectionForm) carry this standalone selector right
 * after the total on their summary row (GiftSummaryRow, owner call 2026-10-23).
 *
 * Shown only when the form's DisplayCurrencyProvider says a currency other
 * than USD can be shown (`conversionEnabled`: on for this org AND a fresh table
 * loaded), the org lets donors choose (`donorCanChoose`), and there is more
 * than one currency to pick. Otherwise it renders nothing — and
 * `CurrencySelectorRow` renders its children exactly as given, so a form whose
 * org is off (every org today) keeps byte-identical markup.
 *
 * A native <select> with an overlaid chevron, the live donor pattern in
 * Util/ProductSelectionButtons.jsx, styled through donor tier-2 tokens only.
 * `setDisplayCurrency` is its only write; it never computes money. A real
 * switch emits CURRENCY_SWITCHED with exactly { from, to }; the form context
 * comes from the registered super-properties.
 */
import React, { useId, useRef, useState } from 'react';
import { trackForm, FORM_EVENTS, SelectChevron } from '@charitystack/shared';
import { useDisplayCurrency } from './DisplayCurrencyProvider';

export function useShowCurrencySelector() {
  const { conversionEnabled, donorCanChoose, availableCurrencies } = useDisplayCurrency();
  return conversionEnabled === true && donorCanChoose === true && availableCurrencies.length > 1;
}

// The one write both pickers make: a real switch sets the provider and emits
// CURRENCY_SWITCHED with exactly { from, to }; re-picking the shown code does nothing.
function switchCurrency(from, to, setDisplayCurrency) {
  if (from === to) return;
  setDisplayCurrency(to);
  try {
    trackForm(FORM_EVENTS.CURRENCY_SWITCHED, { from, to });
  } catch (error) {
    console.error('[CurrencySelector] Analytics tracking failed (non-blocking):', error);
  }
}

export default function CurrencySelector({ elementID, className = '' }) {
  const { displayCurrency, setDisplayCurrency, availableCurrencies } = useDisplayCurrency();
  const show = useShowCurrencySelector();
  // The overlay renders a form twice (desktop pane and drawer): a per-instance
  // infix keeps the label bound to its own select (see FundsDropdown, A11-003).
  const instance = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  if (!show) return null;

  const idBase = `${elementID}-CS-currency-selector`;
  const selectId = `${idBase}-${instance}-select`;
  const onChange = (event) => switchCurrency(displayCurrency, event.target.value, setDisplayCurrency);

  return (
    <div className={`relative inline-flex shrink-0 ${className}`.trim()} id={idBase}>
      <label htmlFor={selectId} className="sr-only">
        Currency
      </label>
      <select
        id={selectId}
        value={displayCurrency}
        onChange={onChange}
        className="dynamic-input-text appearance-none cursor-pointer py-1 pl-[var(--cs-input-padding-x)] pr-8 border rounded-[var(--cs-input-radius)] border-[color:var(--cs-border-control)] hover:border-[color:var(--cs-border-control-hover)] bg-[color:var(--cs-surface-0)] text-[color:var(--cs-text-primary)] text-[length:var(--cs-input-font-size)] shadow-[shadow:var(--cs-control-shadow)] outline-none focus:outline-none focus:shadow-[shadow:var(--cs-input-focus-shadow)]"
      >
        {availableCurrencies.map((code) => (
          <option key={code} value={code} id={`${idBase}-${instance}-option-${code}`}>
            {code}
          </option>
        ))}
      </select>
      <SelectChevron className="absolute right-2 top-1/2 -translate-y-1/2" id={`${idBase}-chevron`} />
    </div>
  );
}

/**
 * Puts the selector beside a form's label row, and nothing else changes:
 *   - hidden: renders `children` untouched (no wrapper), or nothing without them;
 *   - shown with children: `<div className={className}>{children}<CurrencySelector/></div>`;
 *   - shown without children: the selector alone in its own `className` row.
 */
export function CurrencySelectorRow({
  elementID,
  className = 'flex items-center justify-between gap-2',
  children = null,
}) {
  const show = useShowCurrencySelector();
  if (!show) return children;
  return (
    <div className={className}>
      {children}
      <CurrencySelector elementID={elementID} />
    </div>
  );
}

/**
 * P3 UI-1: the picker as the custom-amount field's currency code. It sits where
 * the plain code sits (the field's right edge) and lists, values and writes
 * exactly what `CurrencySelector` does. It renders only under the same gate;
 * the caller renders the plain code otherwise, so a form whose org is off keeps
 * byte-identical markup.
 *
 * A native <select> named "Currency" (the browser's own list, keyboard and
 * screen-reader behaviour), transparent so the field reads as one control, at
 * the field's font-size dial, with the shared select chevron overlaid and the
 * input focus ring on keyboard focus only (not after a click).
 */
export function CurrencyAffixPicker({ elementID }) {
  const { displayCurrency, setDisplayCurrency, availableCurrencies } = useDisplayCurrency();
  const show = useShowCurrencySelector();
  const instance = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  // Chrome matches :focus-visible on a native <select> after a mouse click, so the ring would
  // show inside the amount field on every click. It shows for keyboard focus only: a click
  // marks the focus, and the mark clears on blur, before any Tab can bring focus back. The mark
  // is taken on focus, from a press in the same task: a click that does not focus the select
  // (Safari on macOS) leaves no mark behind for the next Tab.
  const [pointerFocus, setPointerFocus] = useState(false);
  const pressedRef = useRef(false);
  if (!show) return null;

  const idBase = `${elementID}-CS-currency-picker-${instance}`;
  const onChange = (event) => switchCurrency(displayCurrency, event.target.value, setDisplayCurrency);

  return (
    <div className="absolute right-[var(--cs-space-1)] top-1/2 -translate-y-1/2 inline-flex items-center" id={idBase}>
      <select
        id={`${idBase}-select`}
        aria-label="Currency"
        value={displayCurrency}
        onChange={onChange}
        data-pointer-focus={pointerFocus || undefined}
        onPointerDown={() => {
          pressedRef.current = true;
          setTimeout(() => { pressedRef.current = false; }, 0);
        }}
        onFocus={() => setPointerFocus(pressedRef.current)}
        onBlur={() => setPointerFocus(false)}
        // cs-allow-hardcoded: the affix's inner spacing has no tier-2 dial. It is not the field's
        // inset (--cs-input-padding-x belongs to the input), and raw p-* spacing is outside the
        // token contract on purpose (contract.md §7.7, D-ratchet). The vertical step keeps the focus
        // ring inside the field; the left step separates the code from the ring's edge. The right
        // padding is the chevron's lane: the field's inset plus the 20 chevron, so the code clears
        // it. Colour, radius, ring and font size all read tier-2 tokens.
        className="dynamic-input-text dynamic-input-affix appearance-none cursor-pointer bg-transparent border-0 py-0.5 pl-1.5 pr-[calc(var(--cs-input-padding-x)+var(--cs-icon-lg))] rounded-[var(--cs-input-radius)] text-[color:var(--cs-text-secondary)] outline-none focus:outline-none [&:focus-visible:not([data-pointer-focus])]:shadow-[shadow:var(--cs-input-focus-shadow)]"
        style={{
          // CON-6/CON-8: the same font-size dial and fallback as the field it sits on.
          fontSize: 'var(--cs-input-font-size, var(--cs-text-base))',
          // cs-allow-hardcoded: no donor line-height token exists; this mirrors the custom-amount
          // field's own inline line height (CustomAmountInput.jsx) so the picker's code and the
          // typed amount share one baseline. A line-height dial is a design-system change (CON-19).
          lineHeight: '1.5rem',
        }}
      >
        {availableCurrencies.map((code) => (
          <option key={code} value={code} id={`${idBase}-option-${code}`}>
            {code}
          </option>
        ))}
      </select>
      {/* The chevron sits on the field's own inline inset, where every donor dropdown puts it
          (SELECT_CHEVRON_FIELD_CLASS); the wrapper already stands one space step in from the edge. */}
      <SelectChevron
        className="absolute right-[calc(var(--cs-input-padding-x)-var(--cs-space-1))] top-1/2 -translate-y-1/2"
        id={`${idBase}-chevron`}
      />
    </div>
  );
}
